// contentStore.js
// Loads + saves visual-builder overrides via the backend. The backend writes
// changes DIRECTLY to frontend/public/site-content.json (plus SQLite). Editing
// REQUIRES AUTHENTICATION — the backend rejects unauthenticated POSTs.

// Always use an absolute backend URL so fetch never resolves to a relative
// "./" path (which would fail / behave unexpectedly).
const RAW_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_BASE = `${RAW_BASE.replace(/\/$/, "")}/api`;

// Token is kept in localStorage for the session.
const TOKEN_KEY = "ve_editor_token";

export function getToken() {
    try {
        return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
        return "";
    }
}

export function setToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {
        /* ignore */
    }
}

export async function login(password) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 10000);
    try {
        const res = await fetch(`${API_BASE}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ password }),
            signal: controller.signal,
        });
        if (!res.ok) throw new Error("Invalid password");
        const data = await res.json();
        setToken(data.token);
        return data.token;
    } finally {
        clearTimeout(t);
    }
}

// Ask the backend whether the current session token belongs to an editor.
// Returns true ONLY for a valid editor token; public gets 401 -> false.
export async function checkEditorSession() {
    const token = getToken();
    if (!token) return false;
    try {
        const res = await fetch(`${API_BASE}/editor/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return false;
        const data = await res.json();
        return !!data.isEditor;
    } catch {
        return false;
    }
}

export async function loadOverrides() {
    try {
        const res = await fetch(`${API_BASE}/site-content`, { cache: "no-store" });
        if (res.ok) {
            const data = await res.json();
            if (data && typeof data === "object") return data;
        }
    } catch (e) {
        console.error("[VisualEditor] failed to load overrides:", e);
    }
    return {};
}

export function applyOverrides(overrides) {
    if (!overrides) return;
    for (const [id, change] of Object.entries(overrides)) {
        const el = findById(id);
        if (!el) {
            console.warn("[VisualEditor] no element for id:", id);
            continue;
        }
        if (change.type === "src" && el.tagName === "IMG") {
            el.src = change.value;
        } else if (change.type === "text") {
            if (el.children.length === 0) {
                el.textContent = change.value;
            } else {
                setFirstTextNode(el, change.value);
            }
        }
    }
}

function setFirstTextNode(el, text) {
    const node = Array.from(el.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length
    );
    if (node) node.textContent = text;
    else el.textContent = text;
}

export function findById(id) {
    if (id.includes("#")) {
        const [tag, testid] = id.split("#");
        return document.querySelector(`${tag}[data-testid="${testid}"]`);
    }
    const path = id.slice(id.indexOf("@") + 1);
    // pathOf builds leaf -> root (element first, body's child last). Reverse so
    // we can walk from document.body down to the element.
    const segs = path.split(">").reverse();
    let current = document.body;
    for (const seg of segs) {
        const [tag, idxStr] = seg.split(":");
        const index = parseInt(idxStr, 10) || 1;
        const matches = Array.from(current.children).filter(
            (c) => c.tagName.toLowerCase() === tag
        );
        current = matches[index - 1];
        if (!current) return null;
    }
    return current;
}

export async function saveOverrides(overrides) {
    const token = getToken();
    if (!token) return false; // not authenticated
    try {
        const res = await fetch(`${API_BASE}/site-content`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ overrides }),
        });
        if (!res.ok) {
            console.error("[VisualEditor] save failed with status", res.status);
            return false;
        }
        return true;
    } catch (e) {
        console.error("[VisualEditor] save error:", e);
        return false;
    }
}
